import { QueryClient, useQuery, useQueryClient } from 'react-query';
import api from 'services';
import type { ApiError } from 'services/api.errors';
import { checkCondition } from 'services/api.errors';
import type {
  ID,
  IndependentIraUser,
  IndependentIraUsersResponse,
  IraAccountsTableUser,
} from 'services/requests';
import { useQueryParams } from 'hooks/routes';
import { findUserInTableItems } from 'utils/findUsersInCachedQueries';
import { formatInputDate, mapCustodianTypeKeyToValue } from 'utils';
import { Parameters } from 'constants/queryParameters';
import { QueryKeys } from 'constants/queryKeys';
import { mapIndependentIraTypeKeyToValue } from 'screens/IndependentIra/IraIndependentIra.utils';
import type { AxiosError } from 'axios';

export const useGetIndependentIraUser = () => {
  const queryClient = useQueryClient();
  const id = useQueryParams(Parameters.independentIraUserId);
  const queryKey = [QueryKeys.independentIraUser, id];

  return useQuery<IndependentIraUser, AxiosError<ApiError, string>>(
    queryKey,
    queryFn(id, queryKey),
    {
      enabled: Boolean(id),
      initialData: getIndependentIraUserFromCache(queryClient, id),
      initialDataUpdatedAt: getUpdateTimeIraUsers(queryClient),
      refetchOnWindowFocus: false,
    },
  );
};

export const useGetIndependentIraUserPrefetch = () => {
  const queryClient = useQueryClient();

  return async (id: ID) => {
    const queryKey = [QueryKeys.independentIraUser, id];

    await queryClient.prefetchQuery(queryKey, queryFn(id, queryKey), {
      staleTime: 1000,
      initialData: getIndependentIraUserFromCache(queryClient, id),
      initialDataUpdatedAt: getUpdateTimeIraUsers(queryClient),
    });
  };
};

function getIndependentIraUserFromCache(queryClient: QueryClient, id: ID | undefined) {
  return () => {
    if (id === undefined) return;

    const cachedQueries = queryClient.getQueriesData<IndependentIraUsersResponse>({
      queryKey: [QueryKeys.independentIraUsers],
    });

    const user = findUserInTableItems<IraAccountsTableUser, IndependentIraUsersResponse>(
      cachedQueries,
      id,
    );

    if (user) {
      return {
        ...user,
        profileType: mapIndependentIraTypeKeyToValue(user.userProfileType),
        custodianType: mapCustodianTypeKeyToValue(user.custodianType),
      };
    }
    return;
  };
}

function getUpdateTimeIraUsers(queryClient: QueryClient) {
  return () => queryClient.getQueryState(QueryKeys.independentIraUsers)?.dataUpdatedAt;
}

function queryFn(id: ID | undefined, queryKey?: string | unknown[]) {
  return async () => {
    checkCondition(id, queryKey);
    const { data } = await api.getIndependentIraUser(id);
    return {
      ...data,
      profileType: mapIndependentIraTypeKeyToValue(data?.profileType),
      custodianType: mapCustodianTypeKeyToValue(data.custodianType),
      nextBillingDate: data.nextBillingDate
        ? formatInputDate(new Date(data.nextBillingDate)).replace(/[^\d]/g, '')
        : undefined,
    };
  };
}
